/**
 * Die **Teilung** einer Anlage an einen weiteren Nutzer (HM-160, ADR 0043).
 *
 * Eine Anlage hat genau einen **Besitzer** — den `owner` ihres Accounts — und
 * darf mit beliebig vielen Nutzern geteilt werden. Die Teilung gilt genau
 * dieser Anlage und keiner anderen desselben Accounts; sie hängt am Nutzer und
 * an der Anlage, nicht an der Zugehörigkeit (dieselbe Trennung wie die
 * Bewohnung, ADR 0026 §3).
 *
 * **Zwei Stufen, und beide sind Teilmengen dessen, was der Besitzer darf:**
 *
 * - `read` sieht, was der Besitzer sieht — Anlage, Zähler, Messkonzept,
 *   Geräte, Werte, auch die der Wohneinheiten (Entscheidung des
 *   Auftraggebers, OQ-043).
 * - `write` darf darüber hinaus ändern: Stammdaten, Zähler, Messkonzept,
 *   Geräte koppeln und konfigurieren, die Wallbox steuern.
 *
 * **Keine** Stufe darf löschen, archivieren oder freigeben (`data:delete`),
 * und keine darf Berechtigungen vergeben (`member:manage`) — weder eine
 * Teilung noch eine Einladung an einen Bewohner. Das bleibt dem Besitzer.
 */
import type { AccountId, SiteId, SiteShareId, UserId } from "../common/ids.js";
import type { Permission } from "./account.js";

export const SITE_SHARE_ACCESS = ["read", "write"] as const;
export type SiteShareAccess = (typeof SITE_SHARE_ACCESS)[number];

export interface SiteShare {
  readonly id: SiteShareId;
  readonly accountId: AccountId;
  readonly siteId: SiteId;
  readonly userId: UserId;
  readonly access: SiteShareAccess;
  readonly createdAt: Date;
  readonly updatedAt: Date;
}

const READ: readonly Permission[] = [
  "site:read",
  "device:read",
  "meter:read",
  "series:read",
  "concept:read",
  "unit:read",
];

/**
 * Ausgeschrieben statt „alles außer": Eine neue Berechtigung soll eine Teilung
 * nicht still mitbekommen. Wer eine anlegt, entscheidet hier, ob sie dazugehört.
 */
const SHARE_PERMISSIONS: Readonly<Record<SiteShareAccess, readonly Permission[]>> = {
  read: READ,
  write: [...READ, "site:write", "device:provision", "meter:write", "concept:write"],
};

export function permissionsOfShareAccess(access: SiteShareAccess): readonly Permission[] {
  return SHARE_PERMISSIONS[access];
}

export function shareAccessAllows(access: SiteShareAccess, permission: Permission): boolean {
  return SHARE_PERMISSIONS[access].includes(permission);
}
